// lodash
import get from 'lodash/get';
import includes from 'lodash/includes';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import some from 'lodash/some';
//
import { getNonGlobalError } from '../../../utils/error/parseError';
import deliveryConstants, { DELIVERY_DETAILS_FORM_ID } from '../../../constants/deliveryConstants';
import { DELIVERY_ADDRESS_FORM_ID } from '../../../utils/form/configs/deliveryAddress';
import getUndeliverableItems from '../../../utils/delivery/getUndeliverableItems';
import {
  REDUX_HYDRATE,
  GET_DELIVERY_PAGE,
  POST_DELIVERY_PAGE,
  GET_DELIVERY_METHODS,
  RESET_DELIVERY_METHODS,
  PUT_DELIVERY_DETAILS,
  LOGIN_RESET,
  SET_SELECTED_DELIVERY_CHOICE_ID,
  GET_PAYMENT_PAGE,
  INIT_PAYMENT_PAGE,
  PUT_DELIVERY_ADDRESS,
  CHANGE_DELIVERY_ADDRESS,
  POST_ADDRESS_BOOK_CREATE,
  PUT_ADDRESS_BOOK_UPDATE,
  PUT_CC_DELIVERY_DETAILS,
  SET_DELIVERY_BATCHES_PROCESSING,
  SET_EDIT_BASKET_BASE_ROUTE,
  REBATCH_ORDER,
  PUT_ITEM_QUANTITY,
  ROUTER_LOCATION_CHANGE,
  PUT_ITEMS_QUANTITIES,
  SHOW_EMPTY_BASKET_NOTIFICATION,
  SET_SELECTED_SLOT_METHOD,
  SET_SELECTED_DATE_METHOD,
  SET_SELECTED_DATE_METHOD_PAGE,
} from '../../../constants/actionConstants';
import routeConstants from '../../../constants/routeConstants';
import errorCodeConstants from '../../../constants/errorCodeConstants';
import { STORE_SELECTED } from '../../../constants/clickCollectConstants';

export const INITIAL_STATE = {
  pathname: '',
  deliveries: [],
  getDeliveryPageApiCallComplete: false,
  confirmedDeliveryAddress: undefined,
  deliveryOptionsAvailable: {},
  deliveryMethodsApiCallComplete: undefined,
  deliveryCountries: [],
  putDeliveryAddressApiCallActive: false,
  putDeliveryAddressError: undefined,
  saveAddressError: undefined,
  selectedDeliveryChoiceId: undefined,
  showAddressBookModal: false,
  batchesProcessing: false,
  removedServicesWithInfo: [],
  undeliverableItemsInBasket: [],
  shouldReFetchDeliveryOptions: false,
  getBatchesDeliveryMethodsApiCallActive: false,
  // default editBasketModalBaseRoutePath so that delivery page edit basket errors can use it
  editBasketModalBaseRoutePath: routeConstants.DELIVERY,
  showClickCollectNowAvailableMessage: false,
  showAmendQuantityNotification: false,
  usePostToInitDeliveryPage: true,
  // proceedingToPayment covers delivery-details PUT followed by payment-page GET/POST
  proceedingToPayment: false,
};

export function getSelectedButtonId(options) {
  const {
    deliveryOptionsAvailable,
    selectedDeliveryChoiceId,
    defaultDeliveryTabEnabled,
  } = options;

  const {
    clickCollectAvailable,
    collectPlusAvailable,
  } = deliveryOptionsAvailable;

  if (!clickCollectAvailable && !collectPlusAvailable) {
    // only possible choice as collection is unavailable
    return deliveryConstants.DELIVERY;
  }

  if (!selectedDeliveryChoiceId) {
    if (defaultDeliveryTabEnabled) {
      return deliveryConstants.DELIVERY;
    }

    return deliveryConstants.CLICK_AND_COLLECT;
  }

  // will return either an existing choice or the default state of undefined
  return selectedDeliveryChoiceId;
}

function getDeliveryType({ type, countryCode, items }) {

  if (countryCode && countryCode !== 'GB') {
    return deliveryConstants.INTERNATIONAL;
  }

  const hasPreOrderableItems = !!find(items, item => (
    get(item, 'availability.availabilityStatus') === deliveryConstants.PREORDERABLE
  ));

  return hasPreOrderableItems && type !== deliveryConstants.SPECIAL_ORDER ?
    deliveryConstants.SPECIAL_ORDER : type;
}

export const getGetDeliveryMethodsError = (deliveryType, firstAvailableDates, methodId) => {
  const noSlots = firstAvailableDates.length === 0;
  if (deliveryType === deliveryConstants.TRADEPLACE && (noSlots || methodId === '0')) {
    return {
      message: "We're sorry, the products in your order are unavailable",
      bodyLookup: errorCodeConstants.CLIENT_DELIVERY_METHODS_ERROR,
    };
  }
  return undefined;
};

export function getTradeplaceItemsWithUndeliverableQuantity({ deliveries = [], items = [] }) {
  const tradeplaceItemsWithUndeliverableQuantity = [];
  deliveries.forEach((delivery) => {
    if (delivery.type === deliveryConstants.TRADEPLACE) {
      const deliveryItems = items.filter(item => includes(delivery.itemIds, item.id));
      deliveryItems.forEach((item) => {
        if (item.quantity > 1) {
          tradeplaceItemsWithUndeliverableQuantity.push(item);
        }
      });
    }
  });
  return tradeplaceItemsWithUndeliverableQuantity;
}

export function hasClickCollectBecomeAvailable(prevDeliveryOptionsAvailable = {}, deliveryOptionsAvailable = {}) {
  if (isEmpty(prevDeliveryOptionsAvailable)) return false;
  const { clickCollectAvailable: prevClickCollectAvailable } = prevDeliveryOptionsAvailable;
  const { clickCollectAvailable } = deliveryOptionsAvailable;

  return !!(!prevClickCollectAvailable && clickCollectAvailable);
}

function getRemovedServicesWithInfo(orderForm) {
  return get(orderForm, 'removedServices', []);
}

export function deliveryContainsIncompatibleServices(orderForm) {
  const removedServices = getRemovedServicesWithInfo(orderForm);
  return some(removedServices, service => includes(service?.reason, deliveryConstants.INCOMPATIBLE_SERVICE));
}

export default function deliveryReducer(state = INITIAL_STATE, action = {}) {

  switch (action.type) {
    case LOGIN_RESET:
      return INITIAL_STATE;

    case REDUX_HYDRATE: {
      const payload = get(action, 'payload.delivery');
      const deliveries = get(payload, 'deliveries', []);

      const transformedState = {
        ...payload,
        putDeliveryAddressApiCallActive: false,
        deliveries: deliveries.map(delivery => ({
          ...delivery,
          getDeliveryMethodsApiCallActive: false,
        })),
        proceedingToPayment: false,
      };

      return transformedState;
    }

    case `${POST_DELIVERY_PAGE}.LOADING`:
    case `${GET_DELIVERY_PAGE}.LOADING`:
      return {
        ...state,
        getDeliveryPageApiCallComplete: false,
        removedServicesWithInfo: [],
      };

    case `${POST_DELIVERY_PAGE}.FAILED`:
    case `${GET_DELIVERY_PAGE}.FAILED`:
      return {
        ...state,
        getDeliveryPageApiCallComplete: true,
      };

    case `${PUT_ITEM_QUANTITY}.SUCCESS`: {
      const currentItemFocus = get(action, 'itemId', {});
      return {
        ...state,
        currentItemFocus,
      };
    }

    case `${POST_DELIVERY_PAGE}.SUCCESS`:
    case `${GET_DELIVERY_PAGE}.SUCCESS`: {
      const deliveryOptionsAvailable = get(action.result, 'orderForm.deliveryOptions', {});
      const orderForm = get(action.result, 'orderForm') || {};
      const tradeplaceItemsWithUndeliverableQuantity = getTradeplaceItemsWithUndeliverableQuantity(orderForm);

      return {
        ...state,
        usePostToInitDeliveryPage: false,
        deliveries: get(state, 'deliveries.length') ? state.deliveries : orderForm.deliveries,
        deliveryOptionsAvailable,
        selectedDeliveryChoiceId: getSelectedButtonId({
          deliveryOptionsAvailable,
          selectedDeliveryChoiceId: state.selectedDeliveryChoiceId,
          defaultDeliveryTabEnabled: get(action, 'defaultDeliveryTabEnabled'),
        }),
        getDeliveryPageApiCallComplete: true,
        removedServicesWithInfo: getRemovedServicesWithInfo(orderForm),
        tradeplaceItemsWithUndeliverableQuantity,
        deliveryContainsIncompatibleServices: deliveryContainsIncompatibleServices(orderForm),
      };
    }

    case `${PUT_ITEMS_QUANTITIES}.SUCCESS`: {
      const orderForm = get(action.result, 'orderForm', {});

      return {
        ...state,
        tradeplaceItemsWithUndeliverableQuantity: getTradeplaceItemsWithUndeliverableQuantity(orderForm),
      };
    }

    case `${GET_PAYMENT_PAGE}.FAILED`:
    case `${INIT_PAYMENT_PAGE}.FAILED`:
      return {
        ...state,
        incompleteDeliveryDetailsError: getNonGlobalError(action),
        proceedingToPayment: false,
      };

    case `${GET_PAYMENT_PAGE}.SUCCESS`:
    case `${INIT_PAYMENT_PAGE}.SUCCESS`:
      return {
        ...state,
        incompleteDeliveryDetailsError: undefined,
        showClickCollectNowAvailableMessage: false,
        showAmendQuantityNotification: false,
        proceedingToPayment: false,
      };

    case CHANGE_DELIVERY_ADDRESS: {
      return {
        ...state,
        confirmedDeliveryAddress: undefined,
        changingAddress: true,
      };
    }

    case SET_SELECTED_DELIVERY_CHOICE_ID:
      return {
        ...state,
        selectedDeliveryChoiceId: action.id,
        incompleteDeliveryDetailsError: undefined,
      };

    case `${PUT_DELIVERY_ADDRESS}.LOADING`:
      return {
        ...state,
        putDeliveryAddressApiCallActive: true,
        shouldReFetchDeliveryOptions: false,
        shouldSetConfirmedDeliveryAddressAgain: false,
      };

    case `${PUT_DELIVERY_ADDRESS}.FAILED`: {
      const address = get(action, 'address');
      const postcode = get(address, 'address.postcode');

      if (get(action, 'error.code') === errorCodeConstants.CLIENT_HANDLE_SERVER_INTERNAL_ERROR) {
        return {
          ...state,
          putDeliveryAddressApiCallActive: false,
        };
      }

      return {
        ...state,
        putDeliveryAddressApiCallActive: false,
        putDeliveryAddressError: getNonGlobalError(
          action,
          state.putDeliveryAddressError, { postcode },
        ),
        confirmedDeliveryAddress: undefined,
      };
    }

    case `${REBATCH_ORDER}.LOADING`: {
      return {
        ...state,
        removedServicesWithInfo: [],
      };
    }

    case `${REBATCH_ORDER}.SUCCESS`:
    case `${PUT_DELIVERY_ADDRESS}.SUCCESS`: {

      const orderForm = get(action, 'result.orderForm', {});
      const deliveries = get(orderForm, 'deliveries', []);
      const items = get(orderForm, 'items', []);

      const undeliverableItemsInBasket = getUndeliverableItems(deliveries, items);

      const transformedDeliveries = deliveries.map((delivery) => {
        const deliveryItems = items.filter(item => includes(delivery.itemIds, item.id));

        return {
          ...delivery,
          items: deliveryItems,
        };
      });

      if (!isEmpty(undeliverableItemsInBasket)) {
        return {
          ...state,
          confirmedDeliveryAddress: action.address,
          deliveries: transformedDeliveries,
          undeliverableItemsInBasket,
          putDeliveryAddressApiCallActive: false,
          showAmendQuantityNotification: action.orderHasBeenRebatched ? true : state.showAmendQuantityNotification,
          changingAddress: false,
        };
      }

      const deliveryOptionsAvailable = get(action.result, 'orderForm.deliveryOptions', {});
      const showClickCollectNowAvailableMessage =
        hasClickCollectBecomeAvailable(state.deliveryOptionsAvailable, deliveryOptionsAvailable);

      const newDeliveryChoiceId = getSelectedButtonId({
        deliveryOptionsAvailable,
        selectedDeliveryChoiceId: state.selectedDeliveryChoiceId,
      });

      const selectedDeliveryChoiceId = isEqual(deliveryOptionsAvailable, state.deliveryOptionsAvailable) ?
        state.selectedDeliveryChoiceId : newDeliveryChoiceId;

      const removedServicesWithInfo = getRemovedServicesWithInfo(orderForm);

      return {
        ...state,
        confirmedDeliveryAddress: action.address,
        deliveries: transformedDeliveries,
        deliveryOptionsAvailable,
        putDeliveryAddressApiCallActive: false,
        undeliverableItemsInBasket: [],
        putDeliveryAddressError: undefined,
        showAmendQuantityNotification: action.orderHasBeenRebatched ? true : state.showAmendQuantityNotification,
        selectedDeliveryChoiceId,
        removedServicesWithInfo: !isEmpty(removedServicesWithInfo) ?
          removedServicesWithInfo :
          state.removedServicesWithInfo,
        deliveryContainsIncompatibleServices: deliveryContainsIncompatibleServices(orderForm),
        changingAddress: false,
        showClickCollectNowAvailableMessage,
      };
    }

    case `${GET_DELIVERY_METHODS}.LOADING`: {
      const delivery = find(state.deliveries, { id: action.batchId });
      const refresh = !!action.refresh;

      return {
        ...state,
        deliveries: state.deliveries.map((d) => {
          if (delivery.id === d.id) {
            if (refresh) {
              return {
                ...d,
                gvfSlotsLoading: true,
              };
            }

            return {
              ...d,
              getDeliveryMethodsApiCallActive: true,
            };
          }
          return d;
        }),
        getBatchesDeliveryMethodsApiCallActive: true,
        shouldReFetchDeliveryOptions: false,
        deliveryMethodsApiCallComplete: false,
      };
    }

    case `${GET_DELIVERY_METHODS}.FAILED`: {
      const address = get(action, 'address');
      const delivery = find(state.deliveries, { id: action.batchId }) || {};
      const postcode = get(address, 'address.postcode');
      const batchUndeliverableServices = get(action, 'error.code') === errorCodeConstants.BATCH_UNDELIVERABLE_SERVICES;

      const deliveries = state.deliveries.map((d) => {
        if (delivery.id === d.id) {
          let returnObj;

          if (batchUndeliverableServices) {
            const undeliverableServicesAffectedItems =
              delivery.items.map(d => ({ deliveryId: delivery.id, id: d.id, name: d.name }));

            returnObj = {
              undeliverableServices: {
                items: undeliverableServicesAffectedItems,
              },
            };
          } else {
            returnObj = {
              // FIXME: This is no longer out of area postcode error
              // Need to check what possible errors can come back GET_DELIVERY_METHODS call.
              getDeliveryMethodsError: getNonGlobalError(
                action,
                d.getDeliveryMethodsError, { postcode },
              ),
            };
          }

          return {
            ...d,
            ...returnObj,
            deliveryMethods: undefined,
            getDeliveryMethodsApiCallActive: false,
            gvfSlotsLoading: false,
            selectedDeliveryMethodId: '',
          };
        }

        return d;
      });

      return {
        ...state,
        deliveries,
        putDeliveryAddressApiCallActive: false,
        confirmedDeliveryAddress: address,
        getBatchesDeliveryMethodsApiCallActive: deliveries.reduce((acc, batch) => (
          !acc ? batch.getDeliveryMethodsApiCallActive : acc
        ), false),
        deliveryContainsUndeliverableServices: batchUndeliverableServices,
        deliveryMethodsApiCallComplete: false,
      };
    }

    case `${GET_DELIVERY_METHODS}.SUCCESS`: {
      const batchId = action.batchId;
      const delivery = find(state.deliveries, { id: batchId });

      const address = action.address || state.confirmedDeliveryAddress;
      const countryCode = get(action.address, 'address.countryCode', '');

      const deliveryType = getDeliveryType({
        countryCode,
        type: delivery.type,
        items: delivery.items,
      });

      const transformedDeliveries = state.deliveries.map((d) => {
        if (d.id === action.batchId) {
          // handles the tradeplace noSlots scenario
          const getDeliveryMethodsError = getGetDeliveryMethodsError(
            deliveryType,
            get(action.result, 'methods[0].fulfilmentOffers.rows', []),
            get(action.result, 'methods[0].methodId', ''),
          );

          let gvfValues = {};
          if (d.type === deliveryConstants.GREEN_VAN_FLEET) {
            const deliveryInstructionsAvailable = get(action.result, 'methods[0].fulfilmentOffers.type')
              === deliveryConstants.DELIVERY_TYPE_CALENDAR;

            gvfValues = {
              dateRange: get(action, 'dateRange', 1),
              deliveryInstructionsAvailable,
              fromDate: action.fromDate ? action.fromDate : d.fromDate,
              gvfSlotsLoading: false,
              selectedDeliveryMethodDetails: action.refresh ? undefined : d.selectedDeliveryMethodDetails,
              items: delivery.items,
              selectedMethodIsExpress: d.selectedMethodIsExpress || false,
            };
          }

          const analytics = get(action, 'result.analytics.checkout.batch', []).find(b => b.batchNum === d.id);

          let dbsDown = false;
          if (!!action.fromDate && get(action.result, 'methods[0].fulfilmentOffers.type') === deliveryConstants.DELIVERY_TYPE_STANDARD) {
            // Customer tried to get earlier/later dates but DBS was down
            dbsDown = true;
          }

          return {
            ...d,
            ...action.result,
            ...analytics && {
              analytics: {
                batchNum: analytics.batchNum,
                deliveryProposition: analytics.deliveryProposition,
                deliveryType: analytics.deliveryType,
              },
            },
            selectedMethod: d.selectedMethod || 0,
            isGiftReceiptAvailable: action.result.giftReceiptAvailable,
            deliveryDetailsFormId: `${DELIVERY_DETAILS_FORM_ID}-${d.id}`,
            getDeliveryMethodsApiCallActive: false,
            deliveryType,
            getDeliveryMethodsError,
            dbsDown,
            ...gvfValues,
          };
        }

        return d;
      });

      const getBatchesDeliveryMethodsApiCallActive = transformedDeliveries.reduce((acc, batch) => (
        !acc ? batch.getDeliveryMethodsApiCallActive : acc
      ), false);

      return {
        ...state,
        confirmedDeliveryAddress: address,
        deliveries: transformedDeliveries,
        getBatchesDeliveryMethodsApiCallActive,
        shouldReFetchDeliveryOptions: false,
        deliveryMethodsApiCallComplete: true,
        deliveryContainsUndeliverableServices: false,
      };
    }

    case `${PUT_DELIVERY_DETAILS}.LOADING`: {
      return {
        ...state,
        proceedingToPayment: true,
        giftMessageValidationFailed: false,
      };
    }

    case `${PUT_DELIVERY_DETAILS}.FAILED`: {
      const giftMessageValidationFailed = includes([
        errorCodeConstants.GIFT_MESSAGE_LINE_COUNT_EXCEEDED,
        errorCodeConstants.GIFT_MESSAGE_CHARACTER_COUNT_EXCEEDED,
      ], get(action, 'error.code', ''));

      return {
        ...state,
        proceedingToPayment: false,
        giftMessageValidationFailed,
      };
    }

    case `${PUT_DELIVERY_DETAILS}.SUCCESS`: {
      return {
        ...state,
        proceedingToPayment: true,
        giftMessageValidationFailed: false,
      };
    }

    case SET_DELIVERY_BATCHES_PROCESSING:
      return {
        ...state,
        batchesProcessing: action.isProcessing,
      };

    case `${POST_ADDRESS_BOOK_CREATE}.FAILED`:
    case `${PUT_ADDRESS_BOOK_UPDATE}.FAILED`: {
      return {
        ...state,
        saveAddressError: getNonGlobalError({
          ...action,
          error: {
            code: errorCodeConstants.SAVE_ADDRESS_FAILED,
          },
        }),
      };
    }

    case `${POST_ADDRESS_BOOK_CREATE}.SUCCESS`:
    case `${PUT_ADDRESS_BOOK_UPDATE}.SUCCESS`: {
      return {
        ...state,
        saveAddressError: undefined,
      };
    }

    case 'DELIVERY_ADDRESS_RESET_ADDRESS_SEARCH': {
      // TODO this may not be being used
      return {
        ...state,
        // TODO: REVIEW: These props are not global...  ie they are per batch
        selectedDeliveryMethodId: '',
        deliveryType: undefined,
      };
    }

    case '@@redux-form/CHANGE': {
      const formId = get(action, 'meta.form', '');
      const fieldId = get(action, 'meta.field', '');

      if (includes(formId, DELIVERY_DETAILS_FORM_ID)) {
        if (fieldId === deliveryConstants.DELIVERY_V3_FIELD) {
          let parsedValues;

          try {
            parsedValues = JSON.parse(action.payload);
          } catch (e) {
            return state;
          }

          return {
            ...state,
            deliveries: state.deliveries.map((delivery) => {
              if (delivery.id === parsedValues.deliveryId) {
                return {
                  ...delivery,
                  selectedDeliveryMethodDetails: parsedValues,
                };
              }

              return delivery;
            }),
          };
        }
      }

      if (formId === DELIVERY_ADDRESS_FORM_ID) {
        if (fieldId === 'id') {
          return {
            ...state,
            saveAddressError: action.payload ? undefined : state.saveAddressError,
          };
        }
      }

      return state;
    }

    case RESET_DELIVERY_METHODS:
      return {
        ...state,
        confirmedDeliveryAddress: undefined,
        incompleteDeliveryDetailsError: undefined,
        deliveries: state.deliveries.map(delivery => ({
          ...delivery,
          methods: undefined,
          //
          getDeliveryMethodsApiCallActive: false,
          getDeliveryMethodsError: undefined,
          isGiftReceiptAvailable: false,
          selectedDeliveryMethodId: '',
          selectedDeliverySlots: {},
        })),
      };

    case ROUTER_LOCATION_CHANGE: {
      const pathname = get(action.payload, 'location.pathname', '');

      if (includes(pathname, routeConstants.EDIT_BASKET)) {
        return {
          ...state,
          showEditBasketModal: true,
        };
      }

      if (includes(pathname, STORE_SELECTED)) {
        return {
          ...state,
          selectedDeliveryChoiceId: deliveryConstants.CLICK_AND_COLLECT,
          showAddressBookModal: false,
          showEditBasketModal: false,
          showSavedCollectionPointsModal: false,
        };
      }

      switch (pathname) {
        case routeConstants.DELIVERY_CHOICE:
          return {
            ...state,
            selectedDeliveryChoiceId: '',
            showEditBasketModal: false,
          };

        case routeConstants.DELIVERY: {
          return {
            ...state,
            selectedDeliveryChoiceId: deliveryConstants.DELIVERY,
            showAddressBookModal: false,
            showEditBasketModal: false,
          };
        }

        case routeConstants.DELIVERY_OPTIONS:
          return {
            ...state,
            showAddressBookModal: false,
            showEditBasketModal: false,
            selectedDeliveryChoiceId: deliveryConstants.DELIVERY,
          };

        case routeConstants.CLICK_AND_COLLECT_SEARCH_LIST:
        case routeConstants.CLICK_AND_COLLECT_SEARCH_MAP:
          return {
            ...state,
            selectedDeliveryChoiceId: deliveryConstants.CLICK_AND_COLLECT,
            showSavedCollectionPointsModal: false,
            showEditBasketModal: false,
          };

        case routeConstants.DELIVERY_ADDRESS_BOOK:
          return {
            ...state,
            showAddressBookModal: true,
          };

        case routeConstants.DELIVERY_ADDRESS_BOOK_NEW:
          return {
            ...state,
            showAddressBookModal: true,
          };

        case routeConstants.DELIVERY_ADDRESS_BOOK_EDIT:
          return {
            ...state,
            showAddressBookModal: true,
          };

        case routeConstants.CLICK_AND_COLLECT_SAVED_COLLECTION_POINTS:
          return {
            ...state,
            showSavedCollectionPointsModal: true,
          };

        default:
          return {
            ...state,
            showAddressBookModal: false,
            showEditBasketModal: false,
            showSavedCollectionPointsModal: false,
          };
      }
    }

    case `${PUT_CC_DELIVERY_DETAILS}.SUCCESS`: {
      return {
        ...state,
        isClickCollectHeadOffice: action.collectionPoint.isHeadOffice,
        shouldSetConfirmedDeliveryAddressAgain: true,
      };
    }

    case SET_EDIT_BASKET_BASE_ROUTE:
      return {
        ...state,
        editBasketModalBaseRoutePath: action.baseRoutePath,
      };

    case SHOW_EMPTY_BASKET_NOTIFICATION:
      return {
        ...state,
        showEditBasketModal: false,
      };

    case SET_SELECTED_DATE_METHOD: {
      const deliveryToUpdate = find(state.deliveries, { id: action.delivery.id });

      return {
        ...state,
        deliveries: state.deliveries.map((d) => {
          if (deliveryToUpdate.id === d.id) {
            return {
              ...d,
              selectedMethod: action.displayMethod,
            };
          }

          return d;
        }),
      };
    }

    case SET_SELECTED_SLOT_METHOD: {
      const deliveryToUpdate = find(state.deliveries, { id: action.delivery.id });

      return {
        ...state,
        deliveries: state.deliveries.map((d) => {
          if (deliveryToUpdate.id === d.id) {
            return {
              ...d,
              selectedMethod: action.displayMethod,
              selectedMethodIsExpress: action.express,
            };
          }

          return d;
        }),
      };
    }

    case SET_SELECTED_DATE_METHOD_PAGE: {
      const deliveryToUpdate = find(state.deliveries, { id: action.delivery.id });

      return {
        ...state,
        deliveries: state.deliveries.map((d) => {
          if (deliveryToUpdate.id === d.id) {
            const selectedPages = d.selectedPages || [];
            const methodId = action.method.methodId;

            const pageObject = {
              id: methodId,
              page: action.newPage,
              shouldAnimate: action.shouldAnimate,
            };

            const indexToReplace = findIndex(selectedPages, page => page.id === methodId);

            if (indexToReplace >= 0) {
              selectedPages[indexToReplace] = pageObject;
            } else {
              selectedPages.push(pageObject);
            }

            return {
              ...d,
              selectedPages,
            };
          }

          return d;
        }),
      };
    }

    default:
      return state;
  }

}
